import React, { useContext, useEffect, useState } from 'react'
import { Card, CardBody, Row, Col, Button } from 'react-bootstrap'
import Chart from "react-apexcharts";
import './alertTrend.css'
import Loader3 from '../../../../../CommonElements/Spinner/loader3'
import liveanalyticcontext from '../../../../../_helper/formData/LiveAnalytics/LiveAnalytics'
import AreaService from '../../../../../api/areaService';
import NoDataComp from '../../../../../Components/Common/NoDataComp/NoDataComp';
import { Typeahead } from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.css';

const AlertTrendForSubarea = ({ ref, filters, callApi }) => {
    const [chartData, setChartData] = useState({
        series: [],
        options: {
            chart: {
                type: 'line',
                height: 350,
                toolbar: {
                    show: false
                },
                zoom: {
                    enabled: false
                }
            },
            stroke: {
                curve: 'smooth',
                width: 2
            },
            colors: ['#1E3A8A', '#d97706', '#049aec', "#6B21A8", "#B91C1C", "#54ba4a", "#FF6384", "#36A2EB", "#FFCE56"],
            // dataLabels: {
            //     enabled: true,
            //     style: {
            //         fontSize: '10px',
            //         colors: ["#000"]
            //     }
            // },
            dataLabels: {
                enabled: true,
                formatter: function (val) {
                  return val === 0 ? '' : val;
                },
                style: {
                  fontSize: '8px',
                  colors: ["#000"]
                }
              },

            markers: {
                size: 4,
                hover: {
                    size: 6
                }
            },
            xaxis: {
                categories: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
                labels: {
                    rotate: -45,
                    style: {
                        fontSize: '12px'
                    }
                }
            },
            yaxis: {
                title: {
                    text: 'Alerts',
                    style: {
                        fontSize: '12px'
                    }
                },
                min: 0,
                forceNiceScale: true,
                labels: {
                    formatter: function (value) {
                        return Math.round(value);
                    }
                }
            },
            legend: {
                position: 'bottom',
                horizontalAlign: 'center',
                fontSize: '12px',
                itemMargin: {
                    horizontal: 5,
                    vertical: 0
                },
                onItemClick: {
                    toggleDataSeries: true
                },
                showForSingleSeries: true,
                showForNullSeries: true,
                showForZeroSeries: true
            },
            tooltip: {
                shared: true,
                intersect: false
            }
        }
    });

    const [chartLoader, setChartLoader] = useState(true);
    const { setalerttrenddatacontext } = useContext(liveanalyticcontext);
    const [totalAlerts, setTotalAlerts] = useState(0);
    const [hasData, setHasData] = useState(true);

    // Factory and subareas state
    const [factoryLoader, setFactoryLoader] = useState(true);
    const [allSubareas, setAllSubareas] = useState([]);
    const [selectedSubareas, setSelectedSubareas] = useState([]);

    // Fetch factory data
    const fetchFactoryData = async () => {
        setFactoryLoader(true);
        try {
            const factoryId = JSON.parse(localStorage.getItem('userData'))?.factory?.id || 14;
            const response = await AreaService.GetAllAreas(factoryId);

            if (response && response.data && response.data.data) {
                // Extract all subareas from active areas
                const subareas = [];
                response.data.data.areas
                    .filter(area => area.active)
                    .forEach(area => {
                        area.sub_area.forEach(subarea => {
                            subareas.push({
                                id: subarea.id,
                                name: subarea.name,
                                areaName: area.area,
                                areaOwner: area.areaOwner || 'Unknown',
                                label: `${subarea.name} (${area.area})`
                            });
                        });
                    });

                setAllSubareas(subareas);

                // Select first 4 subareas by default (or all if less than 4 are available)
                if (subareas.length >= 4) {
                    setSelectedSubareas(subareas.slice(0, 4));
                } else if (subareas.length > 0) {
                    setSelectedSubareas(subareas);
                } else {
                    setSelectedSubareas([]);
                }
            }
        } catch (error) {
            console.error('Error fetching factory data:', error);
        } finally {
            setFactoryLoader(false);
        }
    };

    // Function to fetch data from API
    const fetchAlertTrendData = async () => {
        if (selectedSubareas.length < 1 || selectedSubareas.length > 5) {
            setHasData(false);
            setChartLoader(false);
            return;
        }

        setChartLoader(true);

        const payload = {
            weekly: filters?.week || "2025-W20",
            month: filters?.month || "",
            shift: filters?.shifts || "",
            factory_id: JSON.parse(localStorage.getItem('userData'))?.factory?.id || 14,
            user_id: JSON.parse(localStorage.getItem('userData'))?.id || 0,
            sub_area_ids: selectedSubareas.map(subarea => subarea.id)
        };

        try {
            const response = await AreaService.alert_trend_live_analytics_by_sub_area(payload);

            if (response && response.data && response.data.heatmapData) {
                processApiData(response.data.heatmapData);
            } else {
                console.error('Invalid API response format');
                setHasData(false);
            }
        } catch (error) {
            console.error('Error fetching alert trend data:', error);
            setHasData(false);
        } finally {
            setChartLoader(false);
        }
    };

    // Process API data
    const processApiData = (heatmapData) => {
        if (!heatmapData || !heatmapData.categories || !heatmapData.data) {
            console.error('Invalid heatmap data format');
            setHasData(false);
            return;
        }

        try {
            // Extract categories (days of the week)
            const categories = heatmapData.categories;

            // Extract series data (each subarea is a line)
            let seriesData = heatmapData.data.filter(item => item.name !== 'Total');

            // If we have fewer series than selected subareas, we need to ensure all selected subareas are represented
            if (seriesData.length < selectedSubareas.length) {
                console.log('API returned fewer series than selected subareas');

                // Create a map of existing series by name (lowercase for case-insensitive comparison)
                const existingSeriesMap = {};
                seriesData.forEach(series => {
                    existingSeriesMap[series.name.toLowerCase()] = true;
                });

                // Add missing subareas with zero data
                selectedSubareas.forEach(subarea => {
                    const subareaName = subarea.name.toLowerCase();
                    if (!existingSeriesMap[subareaName]) {
                        // This subarea is not in the API response, add it with zeros
                        seriesData.push({
                            name: subarea.name,
                            data: new Array(categories.length).fill(0)
                        });
                        console.log(`Added missing series for ${subarea.name}`);
                    }
                });
            }

            // Calculate total alerts
            const totalAlertsCount = seriesData.reduce((total, series) => {
                return total + series.data.reduce((sum, value) => sum + value, 0);
            }, 0);

            setTotalAlerts(totalAlertsCount);

            // Check if we have any data
            const hasAnyData = seriesData.some(series =>
                series.data.some(value => value > 0)
            );

            setHasData(hasAnyData);

            // Update context if needed
            setalerttrenddatacontext(seriesData);

            // Update chart data with enhanced legend options
            setChartData({
                ...chartData,
                series: seriesData,
                options: {
                    ...chartData.options,
                    xaxis: {
                        ...chartData.options.xaxis,
                        categories: categories
                    },
                    legend: {
                        ...chartData.options.legend,
                        position: 'bottom',
                        horizontalAlign: 'center',
                        fontSize: '12px',
                        itemMargin: {
                            horizontal: 5,
                            vertical: 0
                        },
                        onItemClick: {
                            toggleDataSeries: true
                        },
                        showForSingleSeries: true,
                        showForNullSeries: true,
                        showForZeroSeries: true
                    }
                }
            });
        } catch (error) {
            console.error('Error processing API data:', error);
            setHasData(false);
        }
    };

    // Handle subarea selection change
    const handleSubareaChange = (selected) => {
        setSelectedSubareas(selected);
    };

    // Select all subareas
    const handleSelectAllSubareas = () => {
        if (selectedSubareas.length === allSubareas.length) {
            // If all are selected, clear selection
            setSelectedSubareas([]);
        } else {
            // Otherwise select all
            setSelectedSubareas([...allSubareas]);
        }
    };

    // Fetch factory data on component mount
    useEffect(() => {
        fetchFactoryData();
    }, []);

    // Fetch alert trend data when selected subareas or filters change
    useEffect(() => {
        if (!factoryLoader && selectedSubareas.length > 0) {
            fetchAlertTrendData();
        }
    }, [selectedSubareas, filters, callApi, factoryLoader]);

    return (
        <Card ref={ref} style={{ borderRadius: '32px', minHeight: '400px' }}>
            <h5 className="px-4 pt-3" style={{ fontWeight: '500', color: '#383838' }}>
                Subarea Alerts Trend
                <div>
                    <span style={{ fontSize: '15px' }}>Total Alerts: {chartLoader ? '...' : totalAlerts}</span>
                </div>
            </h5>

            <div className="px-4 pb-2">
                <Row className=" d-flex flex-column">
                    <Col xs={12} >
                        <div className="mb-2">
                            <small className="text-muted" style={{ fontSize: '12px' }}>Select 1-5 subareas to analyze:</small>
                            <Typeahead
                                id="subarea-selector"
                                labelKey="label"
                                multiple
                                options={allSubareas}
                                placeholder="Select subareas..."
                                selected={selectedSubareas}
                                onChange={(selected) => {
                                    // Limit selection to 5 items
                                    if (selected.length <= 5) {
                                        handleSubareaChange(selected);
                                    } else {
                                        // If more than 5 are selected, keep only the first 5
                                        handleSubareaChange(selected.slice(0, 5));
                                    }
                                }}
                                disabled={factoryLoader}
                                renderMenuItemChildren={(option) => (
                                    <div style={{ fontSize: '12px' }}>
                                        <strong>{option.name}</strong> <span className="text-muted">({option.areaName})</span>
                                    </div>
                                )}
                                style={{ fontSize: '12px' }}
                                inputProps={{ style: { fontSize: '12px' } }}
                                className="small-typeahead"
                                maxHeight="300px"
                                paginate={true}
                                paginationText="Display more options..."
                                renderToken={(option, { onRemove }, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            display: 'inline-block',
                                            margin: '2px',
                                            padding: '2px 8px',
                                            borderRadius: '4px',
                                            backgroundColor: '#e9ecef',
                                            fontSize: '12px'
                                        }}
                                    >
                                        {option.name}
                                        <button
                                            type="button"
                                            onClick={() => onRemove(option)}
                                            style={{
                                                border: 'none',
                                                background: 'none',
                                                fontSize: '12px',
                                                padding: '0 4px',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            ×
                                        </button>
                                    </div>
                                )}
                            />
                            {selectedSubareas.length < 1 && (
                                <small className="text-danger" style={{ fontSize: '12px' }}>Please select at least 1 subarea</small>
                            )}
                            {selectedSubareas.length > 5 && (
                                <small className="text-danger" style={{ fontSize: '12px' }}>Please select no more than 5 subareas</small>
                            )}
                        </div>
                    </Col>
                    {/* <Col xs={12}>
                        <Button
                            variant={selectedSubareas.length === allSubareas.length ? "danger" : "primary"}
                            size="sm"
                            className="mb-2 w-100"
                            onClick={handleSelectAllSubareas}
                            disabled={factoryLoader}
                        >
                            {selectedSubareas.length === allSubareas.length ? "Clear All" : "Select All"}
                        </Button>
                    </Col> */}
                </Row>
            </div>

            {factoryLoader || chartLoader ? (
                <div className='w-100 h-100 d-flex justify-content-center align-items-center' style={{ minHeight: '250px' }}>
                    <Loader3 />
                </div>
            ) : !hasData || selectedSubareas.length < 1 || selectedSubareas.length > 5 ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '250px' }}>
                    <div className="text-center">
                        <div className="mb-3">
                            <span style={{ backgroundColor: '#54BA4A', color: 'white', borderRadius: '50%', width: '40px', height: '40px', display: 'inline-flex', justifyContent: 'center', alignItems: 'center', marginBottom: '15px' }}>
                                <i className="fa fa-check" style={{ fontSize: '24px' }}></i>
                            </span>
                        </div>
                        <h3 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '10px' }}>
                            {selectedSubareas.length < 1 ? "Select at least 1 subarea" :
                             selectedSubareas.length > 5 ? "Select no more than 5 subareas" :
                             "All Clear!"}
                        </h3>
                        <p style={{ fontSize: '14px', marginBottom: '10px' }}>
                            {selectedSubareas.length < 1 ? "Please select at least 1 subarea to view the trend chart" :
                             selectedSubareas.length > 5 ? "Please select no more than 5 subareas to view the trend chart" :
                             "No alerts detected for the selected subareas in this time period."}
                        </p>
                    </div>
                </div>
            ) : (
                <CardBody>
                    <Chart
                        options={chartData.options}
                        series={chartData.series}
                        type="line"
                        height={'220px'}
                    />
                </CardBody>
            )}
        </Card>
    )
}

export default AlertTrendForSubarea





